import React, { useRef } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

const getEduLabel = (value) => {
  switch (value) {
    case "E0":
      return "All Education Categories";
    case "E1":
      return "Less than high school";
    case "E2":
      return "High school or equivalent";
    case "E3":
      return "Some college / Associate degree";
    case "E4":
      return "Bachelor's or advanced degree";
    case "E5":
      return "Educational attainment not available";
    default:
      return "Unknown";
  }
};

export default function EducationGenderChangeChart({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* ======================
     SUMMARY CALCULATIONS
  ======================= */

  const totalFemaleGrowth = data.reduce(
    (sum, row) => sum + Number(row.change_female || 0),
    0,
  );

  const totalMaleGrowth = data.reduce(
    (sum, row) => sum + Number(row.change_male || 0),
    0,
  );

  const topGrowth = data.reduce(
    (max, row) =>
      Number(row.change_female || 0) + Number(row.change_male || 0) > max.total
        ? {
            label: getEduLabel(row.education_label),
            total:
              Number(row.change_female || 0) + Number(row.change_male || 0),
          }
        : max,
    {
      label: getEduLabel(data[0].education_label),
      total:
        Number(data[0].change_female || 0) + Number(data[0].change_male || 0),
    },
  );

  /* ======================
     CHART DATA
  ======================= */

  const chartData = {
    labels: data.map((d) => getEduLabel(d.education_label)),
    datasets: [
      {
        label: "Female 2018",
        data: data.map((d) => d.female_2018),
        backgroundColor: "#EC4899",
        borderRadius: 6,
      },
      {
        label: "Female 2024",
        data: data.map((d) => d.female_2024),
        backgroundColor: "#F97316",
        borderRadius: 6,
      },
      {
        label: "Male 2018",
        data: data.map((d) => d.male_2018),
        backgroundColor: "#0ea5e9",
        borderRadius: 6,
      },
      {
        label: "Male 2024",
        data: data.map((d) => d.male_2024),
        backgroundColor: "#10b981",
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Education Gender Change (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  /* ======================
     PDF FUNCTION (Same Style)
  ======================= */

  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        const logoWidth = 20; // ideal header width
        const logoHeight = 20; // adjust ratio if needed

        pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY,
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right",
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // Add more pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Final footer
      // ===== FINAL FOOTER SAFE AREA =====

      const footerTopY = pageHeight - 40; // start footer 40mm from bottom
      const footerContentY = pageHeight - 28; // logos row
      const footerBottomTextY = pageHeight - 10; // bottom text row

      // Top Divider Line
      pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

      // UtahData logo
      const utahWidth = 18;
      const utahHeight = 18;
      pdf.addImage(
        utahData,
        "PNG",
        20,
        footerContentY - 8,
        utahWidth,
        utahHeight,
      );

      // Built by
      pdf.setFontSize(10);
      pdf.text("Built by", 45, footerContentY);

      // BODEX logo
      const bodexWidth = 36;
      const bodexHeight = bodexWidth * 0.3;
      pdf.addImage(
        bodex,
        "PNG",
        60,
        footerContentY - 6,
        bodexWidth,
        bodexHeight,
      );

      // Using text
      pdf.text("using", 102, footerContentY);

      // KPI logo
      pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

      // Version
      pdf.text("20250916.v3.198", 140, footerContentY);

      // Bottom Info Line
      pdf.setFontSize(8);
      pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
      pdf.text(
        `Downloaded on: ${new Date().toLocaleDateString()}`,
        pageWidth - 10,
        footerBottomTextY,
        null,
        null,
        "right",
      );

      pdf.save("education_gender_change.pdf");
    });
  };

  /* ======================
     UI
  ======================= */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Education Gender Change (2018–2024)
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-pink-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Female Growth
            </p>
            <p className="text-xl font-bold text-pink-700">
              {totalFemaleGrowth.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Male Growth
            </p>
            <p className="text-xl font-bold text-blue-700">
              {totalMaleGrowth.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Growth Category
            </p>
            <p className="text-lg font-bold text-green-800">
              {topGrowth.label}
            </p>
          </div>
        </div>

        {/* TABLE + CHART */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Education</th>
                  <th className="p-2">F 2018</th>
                  <th className="p-2">F 2024</th>
                  <th className="p-2">Δ F</th>
                  <th className="p-2">M 2018</th>
                  <th className="p-2">M 2024</th>
                  <th className="p-2">Δ M</th>
                </tr>
              </thead>
              <tbody>
                {data.map((row, index) => (
                  <tr key={index} className="hover:bg-gray-100">
                    <td className="p-2 font-medium">
                      {getEduLabel(row.education_label)}
                    </td>
                    <td className="p-2">
                      {Number(row.female_2018).toLocaleString("en-US")}
                    </td>
                    <td className="p-2">
                      {Number(row.female_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2 font-semibold text-pink-600">
                      {row.change_female}
                    </td>
                    <td className="p-2">
                      {Number(row.male_2018).toLocaleString("en-US")}
                    </td>
                    <td className="p-2">
                      {Number(row.male_2024).toLocaleString("en-US")}
                    </td>
                    <td className="p-2 font-semibold text-blue-600">
                      {row.change_male}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* CHART */}
          <div className="flex flex-col w-full bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
